import { useState, useEffect } from 'react';
import PaymentGatewayModal from '../../../components/feature/PaymentGatewayModal';

const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    nameKr: '베이직',
    emoji: '☀️',
    price: 9.99,
    originalPrice: null as number | null,
    period: 'one-time',
    desc: '지금 당장 오늘의 운세가 궁금하다면',
    badge: null as string | null,
    highlight: false,
    isLaunch: false,
    features: [
      { label: '오늘의 운세 리포트', included: true },
      { label: '기본 성향 분석', included: true },
      { label: '오행 요약', included: true },
      { label: '사주팔자 완전 분석', included: false },
      { label: '대운 + 월별 운세', included: false },
      { label: '서양 점성술 통합', included: false },
      { label: '주역 해석', included: false },
      { label: 'AI 1:1 심층 상담', included: false },
    ],
    cta: '시작하기',
    ctaStyle: 'border border-white/20 text-white hover:bg-white/8',
  },
  {
    id: 'premium',
    name: 'Premium',
    nameKr: '프리미엄',
    emoji: '⭐',
    price: 29.99,
    originalPrice: 59 as number | null,
    period: 'one-time',
    desc: '사주·점성술·주역 세 가지를 하나의 리포트로',
    badge: '🚀 런칭 기념 한정가',
    highlight: true,
    isLaunch: true,
    features: [
      { label: '오늘의 운세 리포트', included: true },
      { label: '기본 성향 분석', included: true },
      { label: '오행 요약', included: true },
      { label: '사주팔자 완전 분석', included: true },
      { label: '대운 + 2026 월별 운세', included: true },
      { label: '서양 점성술 통합', included: true },
      { label: '주역 해석', included: true },
      { label: 'AI 1:1 심층 상담', included: false },
    ],
    cta: '지금 결제하기',
    ctaStyle: 'bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-black font-black',
  },
  {
    id: 'vip',
    name: 'VIP',
    nameKr: 'VIP',
    emoji: '👑',
    price: 99.00,
    originalPrice: null as number | null,
    period: 'one-time',
    desc: '사주 + 점성술 + 주역 3종 복합 엔진 + AI 직접 상담',
    badge: 'ELITE',
    highlight: false,
    isLaunch: false,
    features: [
      { label: '오늘의 운세 리포트', included: true },
      { label: '기본 성향 분석', included: true },
      { label: '오행 요약', included: true },
      { label: '사주팔자 완전 분석', included: true },
      { label: '대운 + 2026 월별 운세', included: true },
      { label: '서양 점성술 통합', included: true },
      { label: '주역 해석', included: true },
      { label: 'AI 1:1 심층 상담 무제한', included: true },
    ],
    cta: 'VIP로 시작하기',
    ctaStyle: 'bg-gradient-to-r from-purple-500 to-violet-600 text-white font-black',
  },
];

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const key = 'destiny-launch-expiry';
    let expiry = parseInt(localStorage.getItem(key) ?? '0', 10);
    if (!expiry || expiry < Date.now()) {
      expiry = Date.now() + 23 * 60 * 60 * 1000 + 47 * 60 * 1000 + 33 * 1000;
      localStorage.setItem(key, String(expiry));
    }
    const tick = () => {
      const diff = Math.max(0, expiry - Date.now());
      setTimeLeft({
        hours: Math.floor(diff / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

function Digit({ val, label }: { val: number; label: string }) {
  return (
    <div className="text-center">
      <div
        className="text-2xl font-black text-primary-gold tabular-nums leading-none"
        style={{ textShadow: '0 0 12px rgba(251,191,36,0.6)', fontVariantNumeric: 'tabular-nums' }}
      >
        {String(val).padStart(2, '0')}
      </div>
      <div className="text-[9px] text-primary-gold/50 uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}

export default function PricingSection() {
  const { hours, minutes, seconds } = useCountdown();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCTAClick = (planId: string) => {
    const plan = PLANS.find((p) => p.id === planId);
    if (!plan) return;
    const planInfo = {
      id: plan.id,
      name: plan.name,
      nameKr: plan.nameKr,
      price: plan.price,
      originalPrice: plan.originalPrice,
      desc: plan.desc,
      features: plan.features.filter((f) => f.included).map((f) => f.label),
      isLaunch: plan.isLaunch,
    };
    localStorage.setItem('destiny-selected-plan', JSON.stringify(planInfo));
    setIsModalOpen(true);
  };

  return (
    <section className="py-20 px-4 relative overflow-hidden" id="pricing">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-8"
        style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/dd32d1dd6ad93a8151c052b24ec190ce.jpg)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070414]/90 via-[#070414]/70 to-[#070414]/90" />

      <div className="relative z-10 max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4"
            style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}
          >
            <i className="ri-vip-crown-line text-primary-gold text-sm"></i>
            <span className="text-xs font-bold text-primary-gold tracking-widest uppercase">Pricing Plans</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: '"Cinzel", serif' }}>
            당신의 운명을 알아볼 준비가 됐나요?
          </h2>
          <p className="text-base text-purple-300/80 max-w-xl mx-auto">
            서양 점성술 · 동양 사주팔자 · 주역을 통합한 AI 운명 분석. 지금 런칭 기념 특가로 시작하세요.
          </p>
        </div>

        {/* Countdown Banner */}
        <div
          className="flex items-center justify-center gap-4 mb-10 mx-auto max-w-lg rounded-2xl px-6 py-4"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)' }}
        >
          <div className="flex items-center gap-1.5">
            <i className="ri-timer-flash-line text-primary-gold text-lg animate-pulse"></i>
            <span className="text-xs font-bold text-primary-gold/80 whitespace-nowrap">런칭 기념가 마감까지</span>
          </div>
          <div className="flex items-center gap-2">
            <Digit val={hours} label="시" />
            <span className="text-primary-gold/60 text-xl font-black pb-3">:</span>
            <Digit val={minutes} label="분" />
            <span className="text-primary-gold/60 text-xl font-black pb-3">:</span>
            <Digit val={seconds} label="초" />
          </div>
          <div
            className="px-2.5 py-1 rounded-full text-[10px] font-black text-black whitespace-nowrap"
            style={{ background: '#fbbf24' }}
          >
            -49% OFF
          </div>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {PLANS.map((plan) => {
            const isHighlight = plan.highlight;
            const isVip = plan.id === 'vip';
            const discount = plan.originalPrice
              ? Math.round((1 - plan.price / plan.originalPrice) * 100)
              : 0;

            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-6 flex flex-col transition-all duration-300 cursor-pointer ${
                  selectedPlan === plan.id ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                } ${isHighlight ? 'ring-2 ring-primary-gold' : ''}`}
                style={{
                  background: isHighlight
                    ? 'linear-gradient(135deg, rgba(251,191,36,0.14) 0%, rgba(139,92,246,0.08) 100%)'
                    : isVip
                    ? 'linear-gradient(135deg, rgba(139,92,246,0.12) 0%, rgba(109,40,217,0.06) 100%)'
                    : 'rgba(255,255,255,0.03)',
                  border: isHighlight
                    ? '1px solid rgba(251,191,36,0.45)'
                    : isVip
                    ? '1px solid rgba(139,92,246,0.35)'
                    : '1px solid rgba(255,255,255,0.1)',
                  boxShadow: isHighlight
                    ? '0 0 40px rgba(251,191,36,0.12)'
                    : isVip
                    ? '0 0 40px rgba(139,92,246,0.1)'
                    : 'none',
                }}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {/* Badge */}
                {plan.badge && (
                  <div
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black whitespace-nowrap ${
                      isHighlight ? 'text-black' : 'text-white'
                    }`}
                    style={{
                      background: isHighlight
                        ? 'linear-gradient(90deg, #fbbf24, #f59e0b)'
                        : 'linear-gradient(90deg, #7c3aed, #a855f7)',
                    }}
                  >
                    {plan.badge}
                  </div>
                )}

                {/* Plan name */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{plan.emoji}</span>
                  <span className={`text-xl font-black ${isHighlight ? 'text-primary-gold' : isVip ? 'text-purple-300' : 'text-white'}`}>
                    {plan.name}
                  </span>
                </div>

                {/* Price */}
                <div className="mb-2">
                  {plan.originalPrice && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm line-through text-white/30">${plan.originalPrice}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white bg-rose-500/80">
                        -{discount}%
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-4xl font-black ${isHighlight ? 'text-primary-gold' : isVip ? 'text-purple-300' : 'text-white'}`}
                      style={{ textShadow: isHighlight ? '0 0 20px rgba(251,191,36,0.5)' : undefined }}
                    >
                      ${plan.price}
                    </span>
                    <span className="text-xs text-white/40">USD · 1회</span>
                  </div>
                  {isHighlight && (
                    <p className="text-[10px] text-primary-gold/60 font-semibold mt-0.5">런칭 기념 한정가</p>
                  )}
                </div>

                {/* Desc */}
                <p className="text-xs text-white/60 mb-5 leading-relaxed">{plan.desc}</p>

                {/* Features */}
                <div className="space-y-2 flex-1 mb-6">
                  {plan.features.map((feature) => (
                    <div key={feature.label} className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          feature.included
                            ? isHighlight
                              ? 'bg-primary-gold/20'
                              : isVip
                              ? 'bg-purple-500/20'
                              : 'bg-white/10'
                            : 'bg-transparent'
                        }`}
                      >
                        {feature.included ? (
                          <i className={`ri-check-line text-[10px] ${isHighlight ? 'text-primary-gold' : isVip ? 'text-purple-400' : 'text-white/60'}`}></i>
                        ) : (
                          <i className="ri-close-line text-[10px] text-white/20"></i>
                        )}
                      </div>
                      <span className={`text-xs ${feature.included ? 'text-white/80' : 'text-white/25 line-through'}`}>
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button
                  onClick={(e) => { e.stopPropagation(); handleCTAClick(plan.id); }}
                  className={`w-full py-3 rounded-xl text-sm transition-all hover:scale-105 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${plan.ctaStyle}`}
                  style={
                    isHighlight
                      ? { boxShadow: '0 0 20px rgba(251,191,36,0.3)' }
                      : isVip
                      ? { boxShadow: '0 0 20px rgba(139,92,246,0.3)' }
                      : {}
                  }
                >
                  <i className="ri-secure-payment-fill"></i>
                  {plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        {/* Bottom trust row */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-purple-400/50">
          {[
            { icon: 'ri-shield-check-fill', label: 'SSL 256-bit 보안 결제', color: 'text-emerald-400' },
            { icon: 'ri-paypal-fill', label: 'PayPal 구매자 보호', color: 'text-cyan-400' },
            { icon: 'ri-refund-2-line', label: '즉시 리포트 발급', color: 'text-primary-gold/60' },
            { icon: 'ri-customer-service-2-line', label: '24/7 고객 지원', color: 'text-purple-400' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <i className={`${item.icon} ${item.color} text-sm`}></i>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentGatewayModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}
